import React, {
  ComponentProps,
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useReducer,
} from 'react'
import NiceModal from '@ebay/nice-modal-react'
import { ModalResolveResult } from './utils'

export interface ModalProps {
  /** Modal id */
  id?: string;
}

export interface ModalManagerStore {
  showModal: <TData, TError, C extends FC<any>>(
    modal: C,
    props?: ComponentProps<C> & ModalProps,
  ) => Promise<ModalResolveResult<TData, TError>>;
  removeAllModals: () => void;
}

const ModalManagerContext = createContext<ModalManagerStore>({
  showModal: () => Promise.resolve({} as any),
  removeAllModals: () => {},
});

export const ModalManagerProvider = ({ children }: PropsWithChildren) => {
  const [modals, dispatch] = useReducer(NiceModal.reducer, {});

  const showModal = useCallback(
    async <T, C extends FC<any>>(modal: C, props?: ComponentProps<C> & ModalProps): Promise<T> => {
      // 沒有傳入 id 則直接顯示 modal, 並由 NiceModal 自動產生 id
      if (!props || !props.id) {
        return NiceModal.show(modal, props);
      }

      // 有傳入 id 則先註冊 modal, 再用 id 來顯示 modal
      const modalId = props.id;
      NiceModal.register(modalId, modal, props);
      return NiceModal.show(modalId, props);
    },
    [],
  );

  const removeAllModals = useCallback(() => {
    const modalIds = Object.keys(modals);

    for (const mid of modalIds) {
      // TODO: need to resolve modal before removing it.
      NiceModal.hide(mid);
      NiceModal.remove(mid);
    }
  }, [modals]);

  return (
    <ModalManagerContext.Provider value={{ showModal, removeAllModals }}>
      <NiceModal.Provider modals={modals} dispatch={dispatch}>
        {children}
      </NiceModal.Provider>
    </ModalManagerContext.Provider>
  );
};

export const useModalManager = () => {
  const { showModal, removeAllModals } = useContext(ModalManagerContext);

  return { showModal, removeAllModals };
};
